import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AccountSelector } from "./account-selector";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface SplitEntry {
  id: string;
  description: string;
  amount: number;
  debitAccount: string;
  vatRate: number;
}

interface SplitTransaction {
  id?: string;
  amount: number;
  counterparty?: string;
  date?: string;
  creditAccount?: string;
  mandate_id?: string;
  organization_id?: string;
  transaction_date?: string;
  currency?: string;
  rawData?: any; // Support rawData from ValidationTransaction
}

interface TransactionSplitModalProps {
  transaction: SplitTransaction;
  isOpen: boolean;
  onClose: () => void;
}

export const TransactionSplitModal = ({ transaction, isOpen, onClose }: TransactionSplitModalProps) => {
  const queryClient = useQueryClient();
  const [splitEntries, setSplitEntries] = useState<SplitEntry[]>([
    { id: "1", description: "", amount: 0, debitAccount: "", vatRate: 8.1 },
    { id: "2", description: "", amount: 0, debitAccount: "", vatRate: 8.1 }
  ]);

  const totalAmount = Math.abs(transaction.amount ?? 0);
  const allocatedAmount = splitEntries.reduce((sum, entry) => sum + entry.amount, 0);
  const remainingAmount = totalAmount - allocatedAmount;

  // Mutation to save split transactions
  const saveSplitMutation = useMutation({
    mutationFn: async (entries: SplitEntry[]) => {
      // Extract data from rawData if available (from ValidationTransaction)
      const txData = transaction.rawData || transaction;
      const transactionId = txData.id || transaction.id;
      
      if (!transactionId) throw new Error('Original transaction ID required');
      
      // Delete original transaction
      const { error: deleteError } = await supabase
        .from('transactions')
        .delete()
        .eq('id', transactionId);
      
      if (deleteError) throw deleteError;

      // Insert split transactions
      const splitTransactions = entries.map(entry => ({
        mandate_id: txData.mandate_id || transaction.mandate_id,
        organization_id: txData.organization_id || transaction.organization_id,
        transaction_date: txData.transaction_date || transaction.transaction_date || transaction.date,
        description: entry.description || txData.counterparty_name || transaction.counterparty,
        amount: -Math.abs(entry.amount), // Expense, so negative
        currency: txData.currency || transaction.currency || 'CHF',
        counterparty_name: txData.counterparty_name || transaction.counterparty,
        account_number: entry.debitAccount.split(' - ')[0],
        swiss_vat_rate: entry.vatRate,
        swiss_vat_amount: (entry.amount * entry.vatRate) / 100,
        human_verified: false,
        needs_review: true,
        metadata: {
          split_from: transactionId,
          split_entry_description: entry.description,
        },
      }));

      const { error: insertError } = await supabase
        .from('transactions')
        .insert(splitTransactions);

      if (insertError) throw insertError;
      
      return splitTransactions.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['validation-transactions'] });
      toast.success(`Transaction split into ${count} entries`);
      onClose();
    },
    onError: (error: Error) => {
      toast.error(`Failed to split transaction: ${error.message}`);
    },
  });

  const addSplitEntry = () => {
    const newId = (splitEntries.length + 1).toString();
    setSplitEntries([
      ...splitEntries,
      { id: newId, description: "", amount: 0, debitAccount: "", vatRate: 8.1 }
    ]);
  };

  const removeSplitEntry = (id: string) => {
    setSplitEntries(splitEntries.filter(entry => entry.id !== id));
  };

  const updateSplitEntry = (id: string, field: keyof SplitEntry, value: string | number) => {
    setSplitEntries(splitEntries.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  const handleSave = () => {
    // Validate split entries
    if (remainingAmount !== 0) {
      toast.error('Split amounts must equal total transaction amount');
      return;
    }

    const invalidEntries = splitEntries.filter(
      entry => !entry.description || !entry.debitAccount || entry.amount <= 0
    );

    if (invalidEntries.length > 0) {
      toast.error('All entries must have description, account, and positive amount');
      return;
    }

    saveSplitMutation.mutate(splitEntries);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-space-grotesk text-brand-primary">
            Split Transaction - {transaction.counterparty ?? ""}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Original Transaction Info */}
          <div className="bg-brand-secondary p-4 rounded-lg">
            <h3 className="font-medium text-brand-primary mb-2">Original Transaction</h3>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-brand-text-secondary">Date:</span>
                <p className="font-medium">{transaction.date ?? ""}</p>
              </div>
              <div>
                <span className="text-brand-text-secondary">Counterparty:</span>
                <p className="font-medium">{transaction.counterparty ?? ""}</p>
              </div>
              <div>
                <span className="text-brand-text-secondary">Total Amount:</span>
                <p className="font-medium text-brand-danger">CHF {totalAmount.toFixed(2)}</p>
              </div>
              <div>
                <span className="text-brand-text-secondary">Credit Account:</span>
                <p className="font-medium">{transaction.creditAccount ?? ""}</p>
              </div>
            </div>
          </div>

          {/* Split Entries */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-brand-primary">Split Into Entries</h3>
              <Button
                onClick={addSplitEntry}
                variant="outline"
                size="sm"
                className="bg-brand-secondary hover:bg-brand-accent"
              >
                <i className="fa-solid fa-plus mr-2"></i>Add Entry
              </Button>
            </div>

            <div className="space-y-3">
              {splitEntries.map((entry, index) => (
                <div key={entry.id} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-medium text-sm">Entry {index + 1}</h4>
                    {splitEntries.length > 1 && (
                      <button
                        onClick={() => removeSplitEntry(entry.id)}
                        className="text-brand-danger hover:text-brand-danger/80"
                      >
                        <i className="fa-solid fa-trash text-sm"></i>
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="text-xs text-brand-text-secondary mb-1 block">Description</label>
                      <input
                        type="text"
                        value={entry.description}
                        onChange={(e) => updateSplitEntry(entry.id, 'description', e.target.value)}
                        className="w-full h-8 px-3 bg-white border border-brand-border rounded text-sm"
                        placeholder="Enter description"
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs text-brand-text-secondary mb-1 block">Amount (CHF)</label>
                      <input
                        type="number"
                        value={entry.amount}
                        onChange={(e) => updateSplitEntry(entry.id, 'amount', parseFloat(e.target.value) || 0)}
                        className="w-full h-8 px-3 bg-white border border-brand-border rounded text-sm"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-brand-text-secondary mb-1 block">Debit Account</label>
                      <AccountSelector
                        value={entry.debitAccount}
                        onChange={(account) => updateSplitEntry(entry.id, 'debitAccount', account)}
                      />
                    </div>

                    <div>
                      <label className="text-xs text-brand-text-secondary mb-1 block">VAT Rate</label>
                      <select
                        value={entry.vatRate}
                        onChange={(e) => updateSplitEntry(entry.id, 'vatRate', parseFloat(e.target.value))}
                        className="w-full h-8 px-3 bg-white border border-brand-border rounded text-sm"
                      >
                        <option value="8.1">8.1% (2024+)</option>
                        <option value="2.6">2.6% (Reduced 2024+)</option>
                        <option value="3.8">3.8% (Special 2024+)</option>
                        <option value="7.7">7.7% (Pre-2024)</option>
                        <option value="2.5">2.5% (Pre-2024)</option>
                        <option value="3.7">3.7% (Pre-2024)</option>
                        <option value="0">0%</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-brand-secondary p-4 rounded-lg mt-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-brand-text-secondary">Total Allocated:</span>
                <span className="font-medium">CHF {allocatedAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-brand-text-secondary">Remaining:</span>
                <span className={`font-medium ${remainingAmount > 0 ? 'text-brand-danger' : 'text-brand-success'}`}>
                  CHF {remainingAmount.toFixed(2)}
                </span>
              </div>
              {remainingAmount !== 0 && (
                <p className="text-xs text-brand-warning mt-2">
                  ⚠️ Amounts must total CHF {totalAmount.toFixed(2)}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={remainingAmount !== 0}
              className="bg-brand-primary hover:bg-brand-primary/90"
            >
              Save Split Transaction
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};